import type { ComponentProps, ReactNode } from 'react';

import type { ColorName, ColorVariant, TooltipPosition } from './buttonClasses';
import type { BaseProps, Size, UpdateStatus } from './types';
import type { Style } from './colors';
import { cn } from '../cn';
import { Alerts, General } from '../StrokeIcons';
import { WaveSpinner } from '../WaveSpinner';
import {
  badgeClassName,
  buttonClassName,
  composeVariant,
  softIconColorClasses,
  underlineDecorationClasses,
} from './buttonClasses';
import Tooltip from './Tooltip';
import { sizeIconClass, sizeIconPaddingL, sizeIconPaddingR } from './types';

export type ButtonProps = Omit<ComponentProps<'button'>, 'size'> &
  BaseProps & {
    size?: Size;
    /** Color axis — `'blue' | 'red' | 'green' | …`. Defaults to `'gray'`. */
    color?: ColorName;
    /** Style axis — `'filled' | 'outline' | 'soft' | 'underline'`. Defaults to `'filled'`. */
    tone?: Style;
    active?: boolean;
    tooltip?: ReactNode;
    /** Max width for tooltip in pixels. When set, tooltip text will wrap. */
    tooltipMaxWidth?: number;
    /** Position of the tooltip relative to the button. */
    tooltipPosition?: TooltipPosition;
    /** Extra className applied to the tooltip popover (e.g. to override its background color). */
    tooltipClassName?: string;
    text?: boolean;
    filled?: boolean;
    /** Render with the input/field color system (same palette as Input/Select)
     * instead of the button palette. Used by field-like triggers (FilterSelect,
     * DateButton) so they match adjacent inputs. */
    inputStyle?: boolean;
    readOnly?: boolean;
    badge?: number | string;
    /** Text alignment within the button. Default is 'left'. */
    textAlign?: 'left' | 'center' | 'right';
    /** Status for async operations: 'updating' shows spinner, 'success' shows checkmark, 'error' shows warning. */
    status?: UpdateStatus;
    /** Colored status dot indicator (appears top-right). Used for connection status. */
    dot?: 'success' | 'warning' | 'error';
    /** Remove padding wrapper around icon. When true, defaults to no border. */
    noPadding?: boolean;
  };

export default function Button({
  size = 'md',
  bordered: borderedProp,
  shape,
  icon,
  rIcon,
  error,
  color,
  tone,
  active = false,
  text,
  filled,
  inputStyle,
  readOnly,
  className,
  disabled,
  children,
  tooltip,
  tooltipMaxWidth,
  tooltipPosition,
  tooltipClassName,
  badge,
  textAlign = 'left',
  status,
  dot,
  type = 'button',
  noPadding,
  ...props
}: ButtonProps) {
  // With nothing set, default to the `gray` color (registry-defined neutral button).
  const effectiveVariant: ColorVariant = composeVariant(color ?? 'gray', tone ?? 'filled');
  const hasIcon = !!(icon || status);
  const iconOnly = hasIcon && !children && !rIcon;
  const hasBadge = badge != null && badge !== 0 && badge !== '';
  const dotColorClass =
    dot === 'success' ? 'bg-green-500' : dot === 'warning' ? 'bg-yellow-500' : dot === 'error' ? 'bg-red-500' : '';

  // When noPadding is true, default to no border
  const bordered = noPadding ? false : borderedProp;
  return (
    <Tooltip
      as={readOnly ? 'span' : 'button'}
      tooltip={tooltip}
      tooltipMaxWidth={tooltipMaxWidth}
      tooltipPosition={tooltipPosition}
      tooltipClassName={tooltipClassName}
      variant={effectiveVariant}
      {...(!readOnly && { disabled, type })}
      className={buttonClassName({
        size,
        bordered,
        shape,
        icon: hasIcon,
        rIcon: !!rIcon,
        children: !!children,
        error: !!error,
        variant: effectiveVariant,
        active,
        text,
        filled,
        inputStyle,
        disabled,
        readOnly,
        tooltip: !!tooltip,
        className: cn((hasBadge || dot) && 'relative', className),
        noPadding,
      })}
      {...props}>
      {hasBadge && (
        <span className={badgeClassName(effectiveVariant)}>{typeof badge === 'number' && badge > 99 ? '99+' : badge}</span>
      )}
      {dot && (
        <span
          className={cn(
            'absolute -top-0.5 -right-0.5 size-2 rounded-full ring-2 ring-white dark:ring-slate-800',
            dotColorClass,
          )}
        />
      )}
      {(icon || status) && (
        <span
          className={cn(
            'shrink-0 flex items-center justify-center',
            !noPadding && sizeIconPaddingL(size),
            iconOnly && !noPadding && sizeIconPaddingR(size),
            softIconColorClasses(effectiveVariant),
          )}>
          <span className={cn(sizeIconClass(size), '*:size-full')}>
            {status === 'updating' && <WaveSpinner className="size-full" />}
            {status === 'success' && (
              <>
                <style>{`@keyframes check-pop { 0% { transform: scale(0); } 50% { transform: scale(1.2); } 100% { transform: scale(1); } }`}</style>
                <General.CheckCircle
                  className="size-full text-green-500"
                  style={{ animation: 'check-pop 0.3s ease-out forwards' }}
                />
              </>
            )}
            {status === 'error' && (
              <>
                <style>{`@keyframes error-shake { 0%, 100% { transform: translateX(0); } 20%, 60% { transform: translateX(-2px); } 40%, 80% { transform: translateX(2px); } }`}</style>
                <Alerts.AlertTriangle
                  className="size-full text-red-500"
                  style={{ animation: 'error-shake 0.4s ease-out forwards' }}
                />
              </>
            )}
            {!status && icon}
          </span>
        </span>
      )}
      {children && (
        <span
          className={cn(
            'whitespace-nowrap overflow-hidden text-ellipsis min-w-0 flex grow items-center',
            textAlign === 'center' && 'justify-center',
            textAlign === 'right' && 'justify-end',
            underlineDecorationClasses(effectiveVariant),
          )}>
          {children}
        </span>
      )}
      {rIcon && (
        <span
          className={cn(
            'shrink-0 flex items-center justify-center',
            sizeIconPaddingR(size),
            softIconColorClasses(effectiveVariant),
          )}>
          <span className={cn(sizeIconClass(size), '*:size-full')}>{rIcon}</span>
        </span>
      )}
    </Tooltip>
  );
}
